import React, { useEffect, useState, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { MDBSelect } from 'mdb-react-ui-kit'
import { checkValidity, getErrorMessage, getSuccessMessage, isDirty } from './../shared/validator.function'
import './UISelect.component.scss'

const UISelect = ({
  className,
  disabled,
  id,
  label,
  name,
  size,
  type,
  getValue,
  onValueChange,
  onChange,
  required,
  hint,
  value,
  options,
  onValidityChange,
  rules,
  showValidity,
  search,
  searchLabel,
  dropdown,
  preventFirstSelection,
  optionHeight,
  disablePortal,
  showSelectedSecondaryText,
}) => {
  rules = rules && typeof rules === 'object' ? rules : {}
  if (required) rules.required = true

  const isValid = useMemo(
    () =>
      !rules.required ||
      (Array.isArray(options) ? options : []).filter(o => `${o.value}` === `${value || ''}`).length > 0,
    [rules.required, options, value]
  )

  const [uiState, setUiState] = useState({
    isDirty: false,
    isPristine: true,
    isTouched: false,
    validity: { isValid: false, errors: ['required'] },
  })
  const [opts, setOpts] = useState([])

  const handleValueChange = selectedOption => {
    if (!selectedOption || selectedOption.value === '' || selectedOption.disabled) return

    const asKey = val => (val === null || typeof val === 'undefined' ? '' : `${val}`)
    const currentValue =
      value !== '' && value !== null && typeof value !== 'undefined'
        ? value
        : (Array.isArray(opts) ? opts : []).filter(option => option.checked).map(option => option.value)[0]

    if (asKey(selectedOption.value) === asKey(currentValue)) {
      return
    }

    const event = { target: { name, value: selectedOption.value } }

    setUiState(prevState =>
      !uiState.isDirty && isDirty(value || '', event.target.value)
        ? { ...prevState, isDirty: true, isPristine: false }
        : { ...prevState, isDirty: false }
    )
    ;(typeof getValue === 'function' ? getValue : evt => {})(selectedOption)
    ;(typeof onValueChange === 'function' ? onValueChange : evt => {})(selectedOption)
    ;(typeof onChange === 'function' ? onChange : evt => {})(event)

    checkValidity(event.target.value, rules).then(validity => {
      setUiState(prevState => ({ ...prevState, validity }))
      ;(typeof onValidityChange === 'function' ? onValidityChange : validity => {})(validity)
    })
  }

  const inputProps = {
    className,
    disabled,
    id,
    label,
    name,
    size,
    type,
    onValueChange: handleValueChange,
    required,
    hint,
    preventFirstSelection,
    searchLabel,
    optionHeight,
    disablePortal,
  }
  const emptyInputProps = {
    className,
    disabled,
    id,
    label,
    name,
    size,
    type,
  }
  Object.keys(inputProps)
    .filter(key => typeof inputProps[key] === 'undefined')
    .forEach(key => delete inputProps[key])

  if (inputProps?.label && inputProps?.required && typeof label === 'string') {
    inputProps.label = `${inputProps.label.replace(/[ *]+$/, ' ')} *`
  }

  useEffect(() => {
    checkValidity(value || '', rules).then(validity => {
      setUiState(prevState => ({ ...prevState, validity }))
      ;(typeof onValidityChange === 'function' ? onValidityChange : validity => {})(validity)
    })
  }, [value])
  useEffect(() => {
    if (options && Array.isArray(options)) {
      setOpts(options)
    } else {
      setOpts([])
    }
  }, [options])

  useEffect(() => {
    setUiState(prevState => ({
      ...prevState,
      validity: { isValid, errors: isValid ? [] : ['required'] },
    }))
  }, [isValid])

  const hasValue = value !== '' && value !== null && value !== undefined

  const dataOptions = useMemo(() => {
    const source = Array.isArray(opts) ? opts : []
    const hasChecked = source.some(option => option.checked)

    const mapped = source.map(option => {
      const isSelected = hasValue ? String(option.value) === String(value) : !!option.checked

      return {
        text: option.text,
        secondaryText: option.secondaryText,
        value: option.value,
        ...(isSelected ? { selected: true } : {}),
      }
    })

    return hasValue || hasChecked ? mapped : [{ text: '', value: '', selected: true }, ...mapped]
  }, [opts, value, hasValue])

  const selectedOption = useMemo(
    () =>
      hasValue
        ? dataOptions.find(option => String(option.value) === String(value))
        : dataOptions.find(option => option.selected && option.value !== ''),
    [value, hasValue, dataOptions]
  )

  // MDB5 shows only an option's text in the closed field. Where two options can
  // share a name (carriers), show the secondary text after it, as 1.9 did.
  const selectedDisplayText =
    showSelectedSecondaryText && selectedOption?.secondaryText
      ? `${selectedOption.text} ${selectedOption.secondaryText}`
      : null

  // MDB5 scrolls the open list by index * optionHeight. Rows taller than that
  // (wrapped option text) leave the highlighted option out of view, so scroll the
  // list to its real position once MDB5 has moved the highlight.
  const keepActiveOptionInView = event => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

    requestAnimationFrame(() => {
      const option = document.querySelector('.select-dropdown-container .select-option.active')
      const list = option && option.closest('.select-options-wrapper')
      if (!list) return

      const optionBox = option.getBoundingClientRect()
      const listBox = list.getBoundingClientRect()
      if (optionBox.top < listBox.top) list.scrollTop -= listBox.top - optionBox.top
      else if (optionBox.bottom > listBox.bottom) list.scrollTop += optionBox.bottom - listBox.bottom
    })
  }

  return (
    <div
      onKeyDown={keepActiveOptionInView}
      className={[
        'ui-input',
        'ui-input-select',
        hasValue ? 'has-value' : '',
        uiState.validity.isValid ? 'is-valid' : 'is-invalid',
        uiState.isDirty ? 'is-dirty' : '',
        uiState.isPristine ? 'is-pristine' : '',
        uiState.isTouched ? 'is-touched' : '',
        disabled ? 'is-disabled' : '',
        showValidity ? 'show-validity' : '',
        dropdown ? 'ui-input-dropdown' : '',
        selectedDisplayText ? 'has-selected-display' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {opts && opts.length > 0 ? (
        <div>
          <MDBSelect {...inputProps} search={search} data={dataOptions} value={selectedOption?.value} />
          {selectedDisplayText ? (
            <span className='select-selected-display' aria-hidden='true'>
              {selectedDisplayText}
            </span>
          ) : null}
        </div>
      ) : (
        <MDBSelect {...emptyInputProps} data={[]} />
      )}
      <div className='ui-input-validation'>
        <span className='success-msg'>{showValidity ? getSuccessMessage() : ''}</span>
        <span className='error-msg'>{showValidity ? getErrorMessage(uiState.validity.errors) : ''}</span>
      </div>
    </div>
  )
}

export default observer(UISelect)
